var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNotify } from "react-admin";
import { useTranslation } from "react-i18next";
import { roleContext, useMessagingReadRole } from "@/hooks/useMessagingReadRole";
import { GET_MESSAGING_VOTER_COUNTS, RECOUNT_MESSAGING_VOTERS, } from "@/queries/GetMessagingVoterCounts";
/** How often counts with a recount on its way are read again. */
export var RECOUNT_POLL_MS = 15000;
/**
 * The Recount button: asks for the event's voters to be counted again, says
 * whether it was asked for or already on its way, then calls `onAsked` (to
 * read the counts again). `undefined` without the read permission.
 */
export var useRecountMessagingVoters = function (electionEventId, onAsked) {
    var t = useTranslation().t;
    var notify = useNotify();
    var readRole = useMessagingReadRole();
    var _a = useMutation(RECOUNT_MESSAGING_VOTERS, { context: roleContext(readRole) }), mutate = _a[0], loading = _a[1].loading;
    var recount = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, mutate({ variables: { electionEventId: electionEventId } })];
                case 1:
                    data = (_a.sent()).data;
                    if (!(data === null || data === void 0 ? void 0 : data.messaging_recount_voters)) {
                        notify(t("messaging.voterCount.recountError"), { type: "error" });
                        return [2 /*return*/];
                    }
                    notify(t(data.messaging_recount_voters.requested
                        ? "messaging.voterCount.recountAsked"
                        : "messaging.voterCount.recountAlreadyAsked"), { type: "info" });
                    return [4 /*yield*/, (onAsked === null || onAsked === void 0 ? void 0 : onAsked())];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    console.error(error_1);
                    notify(t("messaging.voterCount.recountError"), { type: "error" });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [mutate, electionEventId, notify, t, onAsked]);
    return {
        recount: readRole && electionEventId ? recount : undefined,
        recounting: loading,
    };
};
/**
 * An event's voters per channel, as last counted, read again every
 * [`RECOUNT_POLL_MS`] while a recount is on its way; with the Recount
 * button's action.
 */
export var useMessagingVoterCounts = function (electionEventId) {
    var readRole = useMessagingReadRole();
    var _a = useQuery(GET_MESSAGING_VOTER_COUNTS, {
        variables: { electionEventId: electionEventId },
        skip: !electionEventId || !readRole,
        context: roleContext(readRole),
    }), data = _a.data, loading = _a.loading, error = _a.error, refetch = _a.refetch, startPolling = _a.startPolling, stopPolling = _a.stopPolling;
    var counts = data === null || data === void 0 ? void 0 : data.messaging_voter_counts;
    var pending = !!(counts === null || counts === void 0 ? void 0 : counts.status.pending);
    useEffect(function () {
        if (!pending)
            return undefined;
        startPolling(RECOUNT_POLL_MS);
        return function () { return stopPolling(); };
    }, [pending, startPolling, stopPolling]);
    var _b = useRecountMessagingVoters(electionEventId, refetch), recount = _b.recount, recounting = _b.recounting;
    return { counts: counts, loading: loading, unavailable: !!error, recount: recount, recounting: recounting, refetch: refetch };
};
